import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, Stack, Drawer } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import CustomDropdown from 'components/Dropdown';
import AdvancedBuilder from './AdvancedBuilder';
import { dropdownSxProps } from './utilities';
import { formatQuery } from 'react-querybuilder';
import { ReactComponent as SaveBlackIcon }  from '../../assets/save_24_black.svg';
import { ReactComponent as SaveBlueIcon }  from '../../assets/save_24_blue.svg';
import { ReactComponent as SavedBlackIcon }  from '../../assets/PepWise2.0/all-saved-black.svg';
import { ReactComponent as SavedBlueIcon }  from '../../assets/PepWise2.0/all-saved-blue.svg';
import MoreOptionMenu from 'components/Dropdown/MoreOptionSecondary';
import SavedQueriesModal from 'components/FineOps/ResourceMapping/SavedQueriesModal';
import CustomSelectDropdown from 'components/Dropdown/CustomSelectDropdown';
import { fetchSavedQueryList } from 'service/service';
import SaveAsModal from 'components/FineOps/ResourceMapping/SaveAsModal';

const initialClearQuery = {
	id: '660802c2-f66d-46ed-a996-24962e922680',
	rules: [
		{
            field: '',
            operator: '',
            value: ''
        },
	]
};

const CostQueryBuilder = ({
	fields,
	queryPreferenceOptions,
	queryPreference,
	setQueryPreference,
	setQueryProps,
	loadOptionsForField,
	fieldValueOptionsCallback,
	valueField,
	setTotalPages,
	sortingInfo,
	setSortingInfo,
	loadOptionsForOperator,
	abortControllerRef,
	setIsAbort,
	isAbort,
	tabValue,
	query,
	setQuery,
	setNoData,
	clearSearchData,
	isRunQuery,
	setIsRunQuery,
	isChartDetailedView,
	setIsReapplied,
	savedQuery = {},
	setSavedQuery,
	customMonth,
	filterMonth,
	setCustomMonth,
	setFilterMonth,
	sectorName,
	setSectorName,
}: any) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [queryPreferenceVal, setQueryPreferenceVal] = useState(queryPreference ?? 'flatlist_programname');
	const [isFormValid, setIsFormValid] = useState(true);
	const [selectedCheckbox, setSelectedCheckbox] = useState<any>([]);
	const [selGroupPaths, setSelGroupPaths] = useState<number[][]>([]);
	const [isSavedQueriesModalOpen, setIsSavedQueriesModalOpen] = useState<boolean>(false);
	const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState<boolean>(false);
	const [savedQueriesCount, setSavedQueriesCount] = useState<number>(0);
	const [selectedValues, setSelectedValues] = useState({
		program: '',
		project: ''
	});

	const stackRef = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState<number | undefined>(undefined); // initial height
	const [isResized, setIsResized] = useState(false);

	const startResize = (e: React.MouseEvent) => {
		e.preventDefault();
		const startY = e.clientY;
		const startHeight = stackRef.current?.offsetHeight || 200;

		const doDrag = (moveEvent: MouseEvent) => {
			const newHeight = startHeight + (moveEvent.clientY - startY);
			const clampedHeight = Math.max(200, newHeight);

			if (clampedHeight <= 200) {
				setIsResized(true); 
				setHeight(undefined); // reset to auto
			} else {
				setIsResized(false); 
				setHeight(clampedHeight);
			}
		};

		const stopDrag = () => {
			document.removeEventListener('mousemove', doDrag);
			document.removeEventListener('mouseup', stopDrag);
		};

		document.addEventListener('mousemove', doDrag);
		document.addEventListener('mouseup', stopDrag);
	};

	const validateForm = () => {
		const hasEmptyField = (rules: any[]): boolean => {
			return rules.some((rule) => {
				if (typeof rule !== 'object') return false;
				if (Array.isArray(rule.rules)) return hasEmptyField(rule.rules);
				return !rule.field || !rule.operator || (rule.operator !== 'null' && rule.operator !== 'notNull' && !rule.value);
			});
		};
		
		if(query.rules.length === 0)
			setIsFormValid(false);
		else
			setIsFormValid(!hasEmptyField(query.rules));
	};

	const handleAbortClick = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort(); 
		}
		setIsAbort(false);
		if(!isRunQuery) {
			// Notify parent to clear data and show "No data"
			if (typeof setNoData === 'function') setNoData(true);
			if (typeof clearSearchData === 'function') clearSearchData();
		}
	}
	const handleRunQuery = () => {
		setIsRunQuery(true);
		setTotalPages(0);
		setSortingInfo({ sortBy: '', sortDir: '' });
		setQueryPreference(queryPreferenceVal);
		// Replace rules with empty field values with a default condition (1 = 1)
		const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
			if (rule.field === "") {
				return { field: "1", operator: "=", value: "1", id: rule.id };
			}
			else if (rule.field === "Year") {
            	return { ...rule, field: 'yearmonth/100' };
        	}
			else if (rule.field === "Month") {
            	return { ...rule, field: 'yearmonth%100' };
        	}
			else if (rule.field === "Program") {
            	return { ...rule, field: 'ProgramName' };
        	}
			return rule;
		});
		const formattedQuery = formatQuery({ ...query, rules: filteredRules }, 'parameterized');
		setQueryProps({
			isSearch: true,
			query: formattedQuery
		});
	};

	const handleClear = () => {
		setIsReapplied(false);
		setQuery(initialClearQuery);
		setSelectedCheckbox([]);
		setSelGroupPaths([]);
	};

	// Query Preference Change
	const handleQueryPrefOnChange = (e: any) => {
		setQueryPreferenceVal(e.target.value);
	}
	
	const handleFieldChange = (fieldKey: string) => {
		if (loadOptionsForField) {
		  loadOptionsForField(fieldKey);
		}
	};
	
	const handleOperatorChange = (operator: string, fieldKey: string) => {
		if (loadOptionsForOperator) {
		  loadOptionsForOperator(operator, fieldKey);
		}
	};

	const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
	};
	
	const handleDetailsThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	// saved query
	const handleSaveSearchQuery = () => {
		setIsSaveAsModalOpen(true);
	};

	const handleAllSavedQueries = () => {
		setIsSavedQueriesModalOpen(true);
	};

	async function getAllSavedQueriesCount() {
		try {
			const isCost = true;
			let res = await fetchSavedQueryList('', '', '', '', isCost);
			if (res.success) {
				setSavedQueriesCount(res.data?.length || 0);
				console.log('logggggg', res.data?.length)
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		getAllSavedQueriesCount();
	}, []);

	useEffect(() => {
		setQueryPreference(queryPreferenceVal);
		// Replace rules with empty field values with a default condition (1 = 1)
		const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
			if (rule.field === "") {
				return { field: "1", operator: "=", value: "1", id: rule.id };
			}
			return rule;
		});
		const formattedQuery = formatQuery({ ...query, rules: filteredRules }, 'parameterized');
		setQueryProps({
			isSearch: true,
			query: formattedQuery
		});
	}, []);

	useEffect(() => {
		validateForm();
		setIsResized(false);
	}, [query]);
		
	useEffect(() => {
		// Set queryPreferenceVal based on tabValue
		if (tabValue === 1) setQueryPreferenceVal('flatlist_sector');
		else if (tabValue === 3) setQueryPreferenceVal('flatlist_project');
		else setQueryPreferenceVal('flatlist_programname');
	}, [tabValue]);

	useEffect(() => {
		setQueryPreferenceVal(queryPreference);
	}, [isChartDetailedView]);

	const isDisabled = useMemo(() => {
		if (query.rules.length === 0) {
			return true;
		}
		return false;
	}, [query]);

	const sizeVal =  isResized ?  '200px' : 'auto';
	return (
		<>
		<Stack 
		ref={stackRef}
			sx={{ borderRadius: '6px',
				backgroundColor: `${colors.queryBuilder.background} !important`,
				color: 'white.main',
				p: 2,
				'& .query-rule-row-combinator-dropdown': {
					position: 'relative',
					bgcolor: 'primary.main'
				},
				'& .query-rule-row-head-icon': {
					zIndex: 2,
					bgcolor: 'primary.main'
				},
				overflow: 'hidden',
				minHeight: '200px',
				height: height ? `${height}px` : sizeVal,
    			position: 'relative',
			}}
		>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={{ xs: 2, sm: 1 }}
				justifyContent="space-between"
				alignItems={{ xs: 'stretch', sm: 'center' }}
				pb={{ xs: 1, sm: 1 }}
				borderBottom={`0.5px solid ${colors.dark.dark}`}
				>
				<Box
					display="flex"
					flexDirection={{ xs: 'column', sm: 'row' }}
					gap={{ xs: 1, sm: 2, md: 4 }}
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					width={{ xs: '100%', sm: 'auto' }}
					mb={{ xs: 1, sm: 0 }}
				>
					<Typography sx={{color:`${colors.queryBuilder.text} !important`, fontSize: { xs: '13px', sm: '15px' } }}>Query Preferences</Typography>
					<CustomSelectDropdown
					options={queryPreferenceOptions.map((item: any) => ({
						value: item.value,
						label: item.label
					}))}
					handleChange={(e) => { handleQueryPrefOnChange(e) }}
					value={queryPreferenceVal}
					boxProps={{
						maxWidth: 400,
						sx: { zIndex: 10, width: { xs: '100%', sm: 'auto' }, minWidth: '300px !important' }
					}}
					selectComponentProps={{
						sx: {
						...dropdownSxProps,
						height: { xs: '32px !important', sm: '32px !important' },
						fontSize: { xs: '13px', sm: '15px' }
						}
					}}
					/>
				</Box>
				</Stack>
				{Object.keys(fields).length > 0 && (
					<AdvancedBuilder
						valueField={valueField}
						fields={fields}
						query={query}
						setQuery={setQuery}
						handleFieldChange={handleFieldChange}
						fieldValueOptionsCallback={fieldValueOptionsCallback}
						onCheckboxChange={setSelectedCheckbox} 
						selectedCheckbox = {selectedCheckbox}
						onGroupPathsChange = {setSelGroupPaths}
						selGroupPaths = {selGroupPaths}
						handleClear = {handleClear}
						isClearDisabled = {isDisabled}
						handleOperatorChange={handleOperatorChange}
						parentHeight={height} 
					/>
				)}
			<Stack direction="row" justifyContent="flex-end" pt={2} borderTop={`0.5px solid ${colors.dark.dark}`}>
				{!isAbort ? <Button
					sx={{border: '1px solid #009BD8',color: 'info.main',fontWeight: '700',
						textTransform: 'uppercase', width: 130, height: 31}}
					variant="pepwiseSecondaryOutline2"
					disabled={(!isFormValid)} 
					onClick={handleRunQuery}
				>
					Run Query
				</Button>
				: <Button
					sx={{border: '1px solid #009BD8',color: 'info.main',fontWeight: '700',
						textTransform: 'uppercase', width: 130, height: 31}}
					variant="pepwiseSecondaryOutline2"
					disabled={(!isFormValid)} 
					onClick={handleAbortClick}
				>
					Abort
				</Button>}
				<MoreOptionMenu sx={{ borderRadius: '4px',  border: 'none', 
					color: 'info.main', width: 30, height: 30, ml: 1 }} 
					isAdvance
					onClick={handleDetailsThreeDotsClick}
					onClose={handleDetailsThreeDotsClose}
					menuItems={[
						{
							key: 'saveAs',
							label: 'Save As',
							icon: <SaveBlackIcon />,
							hoverIcon: <SaveBlueIcon />,
							onMenuClick: handleSaveSearchQuery,
							color: '#000000',
						},
						{
							key: 'allSavedQueries',
							label: 'All Saved Queries',
							icon: <SavedBlackIcon />,
							hoverIcon: <SavedBlueIcon />,
							onMenuClick: handleAllSavedQueries,
							color: '#000000',
							count: savedQueriesCount
						}
						
					]}
					>
				</MoreOptionMenu>
			</Stack>
			<Box
				onMouseDown={startResize}
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					height: '8px',
					width: '100%',
					cursor: 'row-resize',
					zIndex: 999,
				}}
			/>
		</Stack>
		{/* Saved Query */}
		<Drawer anchor="right" open={isSavedQueriesModalOpen} onClose={() => setIsSavedQueriesModalOpen(false)} 
			sx={{
				'& .MuiDrawer-paper': {
				width: '100%',
				maxWidth: 500, 
				backgroundColor: `${colors.overlay.background} !important`,
				},
			}}>
			<SavedQueriesModal setIsSavedQueriesModalOpen={setIsSavedQueriesModalOpen} query = {query} 
			setQuery = {setQuery} setIsReapplied = {setIsReapplied} queryPreference = {queryPreferenceVal} 
			setQueryPreference = {setQueryPreferenceVal} selectedCheckbox = {selectedCheckbox} 
			setSelectedCheckbox = {setSelectedCheckbox} selGroupPaths = {selGroupPaths} 
			setSelGroupPaths = {setSelGroupPaths} isProjectDetails = {false}
			selectedValues = {selectedValues} setSelectedValues = {setSelectedValues} setSavedQuery = {setSavedQuery} isCost = {true}
			customMonth={customMonth}
			filterMonth={filterMonth}
			setCustomMonth={setCustomMonth}
			setFilterMonth={setFilterMonth}
			sectorName={sectorName}
			setSectorName={setSectorName} />
		</Drawer>
		{/* Save As */}
		<Drawer anchor="right" open={isSaveAsModalOpen} onClose={() => setIsSaveAsModalOpen(false)} 
			sx={{
				'& .MuiDrawer-paper': {
				width: '100%',
				maxWidth: 500,
				backgroundColor: `${colors.overlay.background} !important`,
				},
			}}>
			<SaveAsModal setIsSaveAsModalOpen={setIsSaveAsModalOpen} query = {query} 
			queryPreference = {queryPreferenceVal} selectedCheckbox = {selectedCheckbox} 
			selGroupPaths = {selGroupPaths} isProjectDetails = {false}
			onSavedQueriesChange = {getAllSavedQueriesCount}
			isCost={true} />
		</Drawer>
		</>
	);
};

export default CostQueryBuilder;
